import React, { useEffect, useMemo, useState } from "react";
import ActionMenu from "../../Components/ActionMenu";
import ActionIconButton from "../../Components/ActionIconButton";
import ActionsCell from "../../Components/ActionsCell";
import { exportToCsv, commonUserColumns } from "../../utils/exportCsv";
import {
  AlertTriangle,
  ArrowUpCircle,
  Award,
  BookOpen,
  Briefcase,
  Building,
  Calendar,
  CheckCircle,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Edit3,
  Eye,
  FileDown,
  Filter,
  GraduationCap,
  Layers,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Square,
  Trash2,
  TrendingUp,
  Upload,
  User2,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { Card, Modal, message, Spin } from "antd";
import { API_BASE_URL } from "../../config";

// Constants
const server_url = API_BASE_URL;
const GRADE_LIST = [
  "BV1", "BV2", "BV3", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"
];
const colors = {
  orange: "#D67C2C",
  blue: "#2173B7",
  green: "#22c55e",
  purple: "#a855f7"
};

// Helper function for conditional class names
function classNames(...args) {
  return args.filter(Boolean).join(" ");
}

// Badges shown inline before a row defers to the detail modal. Keeps the table
// scannable for teachers who hold twenty-plus classes.
const MAX_TABLE_BADGES = 3;

function TeacherView() {
  // State
  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [errorModalVisible, setErrorModalVisible] = useState(false);

  // Bulk Selection
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Add Teacher Modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [formTeacher, setFormTeacher] = useState({
    name: "",
    email: "",
    password: "",
    grade_levels: [],
  });
  const [showPassword, setShowPassword] = useState(false);

  // Bulk CSV Modal
  const [csvModalOpen, setCSVModalOpen] = useState(false);
  const [csvPreview, setCSVPreview] = useState([]);
  const [csvFile, setCSVFile] = useState(null);

  // CSV Validation State
  const [csvValidation, setCSVValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showSkippedModal, setShowSkippedModal] = useState(false);


  // Detail Modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  // Promote/Update Grades Modal
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [promoteGrades, setPromoteGrades] = useState([]);

  // Bulk Edit Modal
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [bulkEditGrades, setBulkEditGrades] = useState([]);

  // Delete Confirmation Modal
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    open: false,
    type: 'single',
    email: '',
    teachers: [],
    step: 1
  });

  // Toast notification - matching LessonPlanHistory.js
  const [notification, setNotification] = useState(null);

  // Filter state - matching LessonPlanHistory.js
  const [filters, setFilters] = useState({
    grade: "",
    school: "",
    status: "",
    department: ""
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const totalPages = Math.ceil(filteredTeachers.length / pageSize);
  const pagedTeachers = filteredTeachers.slice((page - 1) * pageSize, page * pageSize);

  // Get access token
  const accessToken = localStorage.getItem("accessToken");

  // Fetch teachers with retry mechanism
  useEffect(() => {
    const fetchWithRetry = async () => {
      try {
        await fetchTeachers();
      } catch (error) {
        // If we have retries left and there was an error, try again after a delay
        if (retryCount < 3) {
          console.log(`Retrying fetch (${retryCount + 1}/3)...`);
          setTimeout(() => {
            setRetryCount(prevCount => prevCount + 1);
          }, 1500); // Retry after 1.5 seconds
        } else {
          // If we've exhausted retries, show the error modal
          setErrorModalVisible(true);
        }
      }
    };

    fetchWithRetry();
    // eslint-disable-next-line
  }, [retryCount]);

  // Filter teachers when search or filters change
  useEffect(() => {
    filterTeachers();
    // eslint-disable-next-line
  }, [teachers, search, filters]);

  // Distinct department values for the Department filter dropdown — extracted from
  // every teacher's `departments` array (same distinct-value pattern StudentView.js
  // uses for its Section filter).
  const departmentOptions = useMemo(() => {
    const values = new Set();
    teachers.forEach((t) => {
      (t.departments || []).forEach((d) => { if (d) values.add(d); });
    });
    return Array.from(values).sort();
  }, [teachers]);

  // Filter teachers based on search and filter criteria
  const filterTeachers = () => {
    const filtered = teachers.filter((t) => {
      // Text search
      // Class names, grades, and sections are searchable too, so an admin can
      // find "who teaches 7A" from the same box.
      const classTerms = (t.assigned_classes || []).flatMap((c) => [c.name, c.grade, c.section, c.subject]);
      const departmentTerms = t.departments || [];
      const matchesSearch = !search ||
        [t.name, t.email, t.role, t.current_school, ...(t.grade_levels || []), ...classTerms, ...departmentTerms]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(search.toLowerCase());

      // Grade filter
      const matchesGrade = !filters.grade ||
        (t.grade_levels && t.grade_levels.includes(filters.grade));

      // School filter
      const matchesSchool = !filters.school ||
        t.current_school === filters.school;

      // Department filter
      const matchesDepartment = !filters.department ||
        (t.departments || []).includes(filters.department);

      // Status filter
      const matchesStatus = !filters.status ||
        t.status === filters.status;

      return matchesSearch && matchesGrade && matchesSchool && matchesDepartment && matchesStatus;
    });

    setFilteredTeachers(filtered);
  };

  // Fetch teachers
  async function fetchTeachers() {
    setLoading(true);
    setError(null);

    try {
      if (!accessToken) {
        throw new Error("Authentication token not found");
      }

      const resp = await fetch(`${server_url}/admin/get-teachers`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch teachers: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      setTeachers(Array.isArray(data) ? data : []);
      setFilteredTeachers(Array.isArray(data) ? data : []);
      setError(null);
      setErrorModalVisible(false);
    } catch (err) {
      console.error("Error fetching teachers:", err);
      setError(err.message || "Error fetching teachers");
      setTeachers([]);
      throw err; // Re-throw for retry logic
    } finally {
      setLoading(false);
    }
  }

  // Calculate statistics for the dashboard cards
  const totalTeachers = teachers.length;
  const recentTeachers = teachers.filter(t => {
    if (!t.created_at) return false;
    const createdDate = new Date(t.created_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return createdDate >= thirtyDaysAgo;
  }).length;

  const activeGradeLevels = [...new Set(
    teachers.flatMap(t => t.grade_levels || []).filter(Boolean)
  )].length;

  const teachersWithProfiles = teachers.filter(t => t.profile_picture_url).length;

  // StatsCard component - exactly like LessonPlanHistory.js
  const StatsCard = ({ title, value, icon: Icon, color, bgColor, subtitle }) => (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className="p-3 rounded-full" style={{ backgroundColor: bgColor }}>
            <Icon className="w-6 h-6" style={{ color: color }} />
          </div>
        </div>
      </div>
    </div>
  );

  // Updated dashboardStats array with proper Icon references
  const dashboardStats = [
    {
      icon: Users,
      title: "Total Teachers",
      value: totalTeachers,
      color: colors.blue,
      bgColor: `${colors.blue}10`
    },
    {
      icon: UserCheck,
      title: "Recent Registrations",
      value: recentTeachers,
      subtitle: "Last 30 days",
      color: colors.green,
      bgColor: `${colors.green}10`
    },
    {
      icon: GraduationCap,
      title: "Active Grade Levels",
      value: activeGradeLevels,
      subtitle: "Different grades taught",
      color: colors.purple,
      bgColor: `${colors.purple}10`
    },
    {
      icon: TrendingUp,
      title: "Completed Profiles",
      value: teachersWithProfiles,
      subtitle: `${Math.round((teachersWithProfiles / totalTeachers) * 100) || 0}% completion`,
      color: colors.orange,
      bgColor: `${colors.orange}10`
    }
  ];

  // Bulk Selection Handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTeachers([]);
    } else {
      setSelectedTeachers(pagedTeachers.map(t => t.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectTeacher = (teacherId) => {
    setSelectedTeachers(prev => {
      if (prev.includes(teacherId)) {
        return prev.filter(id => id !== teacherId);
      } else {
        return [...prev, teacherId];
      }
    });
  };

  // Handle filter changes - matching LessonPlanHistory.js style
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  // Clear all filters - matching LessonPlanHistory.js style
  const clearFilters = () => {
    setFilters({
      grade: "",
      school: "",
      status: "",
      department: ""
    });
    setSearch("");
    setPage(1);
  };

  // Retry fetching teachers
  const handleRetryFetch = () => {
    setRetryCount(0); // Reset retry count
    setErrorModalVisible(false); // Close modal
  };

  // Show notification - matching LessonPlanHistory.js
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Create Teacher Handler
  async function handleCreateTeacher(e) {
    e.preventDefault();
    const fd = new FormData();
    fd.append("name", formTeacher.name);
    fd.append("email", formTeacher.email);
    fd.append("password", formTeacher.password);
    fd.append("role", "teacher");
    fd.append("grade_levels", formTeacher.grade_levels.join(","));
    try {
      const resp = await fetch(`${server_url}/admin/create-user`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Failed to create teacher");
      setAddModalOpen(false);
      setFormTeacher({ name: "", email: "", password: "", grade_levels: [] });
      fetchTeachers();
      showNotification("success", "Teacher created successfully!");
    } catch (err) {
      showNotification("error", err.message || "Failed to create teacher");
    }
  }

  // Email validation helper
  const isValidEmail = (email) => {
    if (!email || typeof email !== 'string') return { valid: false, reason: "Empty email" };
    const trimmed = email.trim();
    if (trimmed.includes(' ')) return { valid: false, reason: "Contains spaces" };
    const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!pattern.test(trimmed)) return { valid: false, reason: "Invalid format" };
    return { valid: true, reason: null };
  };

  // Bulk Upload Handlers
  async function handleCSVChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setCSVFile(file);
    setCSVValidation(null);
    setUploadResult(null);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const text = evt.target.result;
      const lines = text.split("\n").filter(line => line.trim());
      if (lines.length < 2) {
        setCSVPreview([]);
        return;
      }

      const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
      const emailIdx = headers.indexOf("email");
      const nameIdx = headers.indexOf("name");

      // Parse and validate rows client-side
      const parsedRows = [];
      const emailSet = new Set();

      for (let i = 1; i < lines.length && i <= 20; i++) {
        const cols = lines[i].split(",").map(c => c.trim());
        const email = emailIdx >= 0 ? cols[emailIdx] || "" : "";
        const name = nameIdx >= 0 ? cols[nameIdx] || "" : "";

        let status = "valid";
        let reason = null;

        const emailCheck = isValidEmail(email);
        if (!emailCheck.valid) {
          status = "invalid_email";
          reason = emailCheck.reason;
        } else if (emailSet.has(email.toLowerCase())) {
          status = "duplicate";
          reason = "Duplicate in file";
        }
        emailSet.add(email.toLowerCase());

        parsedRows.push({
          row: i + 1,
          name,
          email,
          status,
          reason
        });
      }

      setCSVPreview(parsedRows);

      // Validate on server
      if (file) {
        setValidating(true);
        try {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("role", "teacher");

          const resp = await fetch(`${server_url}/admin/bulk-operations/validate-csv`, {
            method: "POST",
            headers: { Authorization: `Bearer ${accessToken}` },
            body: fd,
          });

          if (resp.ok) {
            const validation = await resp.json();
            setCSVValidation(validation);
            if (validation.preview) {
              setCSVPreview(validation.preview);
            }
          } else {
            // Show error message to user
            const errorData = await resp.json().catch(() => ({}));
            const errorMsg = errorData.detail || "CSV validation failed. Please check your file format.";
            showNotification("error", errorMsg);
            setCSVFile(null);
            setCSVPreview([]);
            setCSVValidation(null);
          }
        } catch (err) {
          console.error("Validation error:", err);
          showNotification("error", "Failed to validate CSV file.");
        } finally {
          setValidating(false);
        }

      }
    };
    reader.readAsText(file);
  }

  async function handleBulkUpload(e) {
    e.preventDefault();
    if (!csvFile) return showNotification("error", "No CSV file selected.");
    const fd = new FormData();
    fd.append("role", "teacher");
    fd.append("file", csvFile);
    try {
      const resp = await fetch(`${server_url}/admin/create-user`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Bulk upload failed.");

      setUploadResult(data);

      let successMsg = `Created ${data.created_count || 0} teachers`;
      if (data.skipped_count > 0) {
        successMsg += `. ${data.skipped_count} records were skipped.`;
      }

      setCSVModalOpen(false);
      setCSVFile(null);
      setCSVPreview([]);
      setCSVValidation(null);
      fetchTeachers();

      if (data.skipped_count > 0) {
        showNotification("warning", successMsg);
        setShowSkippedModal(true);
      } else {
        showNotification("success", successMsg);
      }
    } catch (err) {
      showNotification("error", err.message || "Bulk upload failed.");
    }
  }


  function downloadCSVTemplate() {
    const csv = "name,email,password,grade_levels\nJohn Doe,john@example.com,Secret123,\"4,5,6\"";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(blob);
    a.download = "teachers_template.csv";
    a.click();
  }

  // Bulk Edit Handler
  async function handleBulkEdit(e) {
    e.preventDefault();
    if (selectedTeachers.length === 0) return showNotification("error", "No teachers selected.");
    if (!bulkEditGrades.length) return showNotification("error", "Please select grade levels.");

    try {
      const resp = await fetch(`${server_url}/admin/bulk-update-teachers`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          teacher_ids: selectedTeachers,
          grade_levels: bulkEditGrades
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Bulk edit failed.");
      setBulkEditModalOpen(false);
      setBulkEditGrades([]);
      setSelectedTeachers([]);
      setSelectAll(false);
      fetchTeachers();
      showNotification("success", `${selectedTeachers.length} teachers updated successfully!`);
    } catch (err) {
      showNotification("error", err.message || "Bulk edit failed.");
    }
  }

  // Delete confirmation handlers
  const openDeleteConfirmation = (type, email = '', teachers = []) => {
    setDeleteConfirmModal({
      open: true,
      type,
      email,
      teachers,
      step: 1
    });
  };

  const handleDeleteConfirmation = async () => {
    const { type, email, teachers, step } = deleteConfirmModal;

    if (step < 3) {
      setDeleteConfirmModal(prev => ({ ...prev, step: prev.step + 1 }));
      return;
    }

    try {
      if (type === 'single') {
        const resp = await fetch(`${server_url}/admin/delete-user/${email}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || "Failed to delete teacher");
        showNotification("success", "Teacher deleted successfully");
      } else if (type === 'bulk') {
        const resp = await fetch(`${server_url}/admin/bulk-delete-teachers`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ teacher_ids: teachers }),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || "Failed to delete teachers");
        setSelectedTeachers([]);
        setSelectAll(false);
        showNotification("success", `${teachers.length} teachers deleted successfully`);
      }
      setDeleteConfirmModal({ open: false, type: 'single', email: '', teachers: [], step: 1 });
      fetchTeachers();
    } catch (err) {
      showNotification("error", err.message || "Delete failed");
    }
  };

  // Promote Grade Levels
  function openPromote(teacher) {
    setSelectedTeacher(teacher);
    setPromoteGrades(Array.isArray(teacher.grade_levels) ? teacher.grade_levels : []);
    setPromoteModalOpen(true);
  }

  async function handlePromote(e) {
    e.preventDefault();
    const fd = new FormData();
    fd.append("grade_levels", promoteGrades.join(","));
    try {
      const resp = await fetch(
        `${server_url}/admin/teacher/${selectedTeacher.id}/update-grades`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}` },
          body: fd,
        }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Failed to update grade levels");
      setPromoteModalOpen(false);
      fetchTeachers();
      showNotification("success", "Grade levels updated successfully!");
    } catch (err) {
      showNotification("error", err.message || "Failed to update grade levels");
    }
  }

  // UI

  // Export the whole filtered set, not just the page on screen.
  function handleExportCsv() {
    const rows = filteredTeachers;
    if (!rows.length) return showNotification("error", "There is nothing to export.");
    const count = exportToCsv("teachers", [
      ...commonUserColumns,
      { header: "readonly_departments", value: (t) => t.departments },
      { header: "readonly_classes", value: (t) => (t.assigned_classes || []).map((c) => `${[c.grade, c.section].filter(Boolean).join(" ")}${c.subject ? ` (${c.subject})` : ""}`) },
      { header: "readonly_class_count", value: (t) => (t.assigned_classes || []).length },
      { header: "grade_levels", value: (t) => t.grade_levels },
      { header: "current_school", value: (t) => t.current_school },
      { header: "years_of_experience", value: (t) => t.years_of_experience },
      { header: "phone_number", value: (t) => t.phone_number },
    ], rows);
    showNotification("success", `Exported ${count} teachers to CSV.`);
  }


  return (
    <>
      <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

          {/* Notification - exactly like LessonPlanHistory.js */}
          {notification && (
            <div
              className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${notification.type === 'success' ? 'bg-green-500 text-white' :
                notification.type === 'error' ? 'bg-red-500 text-white' :
                  'bg-blue-500 text-white'
                }`} style={{ animation: 'slideInRight 0.5s ease-out' }}>
              {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> :
                notification.type === 'error' ? <AlertTriangle className="w-5 h-5" /> :
                  <AlertTriangle className="w-5 h-5" />}
              <span className="font-[poppins]">{notification.message}</span>
              <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Header - exactly like LessonPlanHistory.js */}
          <div className="pt-8 pb-8 text-center relative overflow-hidden">
            <div className="relative z-10">
              <div>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                  style={{ backgroundColor: '#D67C2C' }}>
                  <Briefcase className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                  Teacher Management Dashboard
                </h1>
                <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                  Comprehensive teacher management system with grade level tracking and bulk operations
                </p>
              </div>
            </div>
          </div>

          {/* Dashboard Cards - exactly like LessonPlanHistory.js */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {dashboardStats.map((stat, index) => (
              <StatsCard
                key={index}
                title={stat.title}
                value={stat.value}
                icon={stat.icon}
                color={stat.color}
                bgColor={stat.bgColor}
                subtitle={stat.subtitle}
              />
            ))}
          </div>

          {/* Filter Section - exactly like LessonPlanHistory.js */}
          <div className="bg-white rounded-2xl shadow-md overflow-visible border border-gray-200 mb-8">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                    <Filter className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Filter Teachers</h2>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setAddModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Create New Teacher
                  </button>

                  <ActionMenu
                    label="Bulk Actions"
                    icon={<Layers className="w-5 h-5" />}
                    items={[
                      {
                        key: "upload",
                        label: "Bulk Upload (CSV)",
                        description: "Create teachers from a CSV, or update those that already exist.",
                        icon: <Upload className="w-4 h-4" />,
                        tone: "primary",
                        onClick: () => setCSVModalOpen(true),
                      },
                      {
                        key: "export",
                        label: "Export CSV",
                        description: "Download every teacher matching the current filters.",
                        icon: <Download className="w-4 h-4" />,
                        tone: "success",
                        onClick: handleExportCsv,
                      },
                    ]}
                  />
                </div>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={(e) => e.preventDefault()}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <Search className="inline-block w-4 h-4 mr-2" />
                    Search
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search by name or email..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                    />
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  </div>
                </div>

                {/* Status filter intentionally left out: the /admin/get-teachers
                    response has no status/is_active field on teacher records, so
                    there is no real data to filter on (see filters.status, which
                    stays wired for forward-compatibility but is never populated by
                    a select here). */}

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <GraduationCap className="inline-block w-4 h-4 mr-2" />
                    Grade
                  </label>
                  <select
                    name="grade"
                    value={filters.grade}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Grades</option>
                    {GRADE_LIST.map((grade) => (
                      <option key={grade} value={grade}>Grade {grade}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <Building className="inline-block w-4 h-4 mr-2" />
                    Department
                  </label>
                  <select
                    name="department"
                    value={filters.department}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Departments</option>
                    {departmentOptions.map((dept) => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-3 lg:col-span-6 flex items-center justify-center space-x-4 mt-4">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Search className="inline-block w-4 h-4 mr-2" />
                    Search
                  </button>

                  <button
                    type="button"
                    onClick={clearFilters}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <RefreshCw className="inline-block w-4 h-4 mr-2" />
                    Clear Filters
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {selectedTeachers.length > 0 && (
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
              <div className="p-6 bg-orange-50 border-b border-orange-100">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="text-orange-700 font-semibold">
                      {selectedTeachers.length} teacher{selectedTeachers.length > 1 ? 's' : ''} selected
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setBulkEditModalOpen(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#D67C2C] text-white font-medium rounded-lg hover:bg-[#c06a25] transition-colors shadow-md"
                    >
                      <Edit3 className="w-10 h-10" />
                      Edit Grade Levels
                    </button>
                    <button
                      onClick={() => openDeleteConfirmation('bulk', '', selectedTeachers)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors shadow-md"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTeachers([]);
                        setSelectAll(false);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors shadow-md"
                    >
                      <X className="w-4 h-4" />
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Teachers Table Section */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">
                  Teachers ({filteredTeachers.length})
                </h2>
              </div>
            </div>

            {/* Table Content */}
            {loading ? (
              <div className="flex justify-center items-center p-12">
                <div className="text-center">
                  <div className="w-8 h-8 text-[#D67C2C] animate-spin rounded-full border-4 border-orange-500 border-t-transparent mx-auto"></div>
                  <p className="mt-4 text-gray-600 font-medium">Loading teachers...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex justify-center items-center p-12 text-red-500">
                <AlertTriangle className="w-6 h-6 mr-2" />
                <span>{error}</span>
                <button
                  onClick={handleRetryFetch}
                  className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : filteredTeachers.length === 0 ? (
              <div className="flex flex-col justify-center items-center p-12 text-gray-500">
                <Briefcase className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg mb-3">No teachers found</p>
                <p className="text-sm text-gray-400 mb-6">
                  {search || filters.status || filters.school || filters.grade || filters.department ?
                    "Try clearing your filters or creating a new teacher." :
                    "Get started by creating your first teacher."}
                </p>
                <button
                  onClick={() => setAddModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Create New Teacher
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button onClick={handleSelectAll} className="flex items-center gap-2 hover:text-[#D67C2C] transition-colors">
                          {selectAll ? <CheckSquare className="w-5 h-5 text-[#D67C2C]" /> : <Square className="w-5 h-5" />}
                          Select
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Teacher Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Classes
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Department
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pagedTeachers.map((t) => (
                      <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleSelectTeacher(t.id)}
                            className="hover:text-[#D67C2C] transition-colors"
                          >
                            {selectedTeachers.includes(t.id) ?
                              <CheckSquare className="w-5 h-5 text-[#D67C2C]" /> :
                              <Square className="w-5 h-5 text-gray-400" />
                            }
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                              {t.profile_picture_url ? (
                                <img
                                  src={server_url + t.profile_picture_url}
                                  alt={t.name}
                                  className="h-11 w-11 rounded-full object-cover border border-gray-200"
                                />
                              ) : (
                                <div className="h-11 w-11 rounded-full bg-[#FFF5EB] flex items-center justify-center">
                                  <User2 className="h-5 w-5 text-[#D67C2C]" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">
                                {t.name || "Unnamed Teacher"}
                              </div>
                              <div className="text-sm text-gray-500 truncate" title={t.email}>
                                {t.email || "No email"}
                              </div>
                              {(t.grade_levels || []).length > 0 && (
                                <div className="mt-0.5 text-xs text-gray-400">
                                  {t.grade_levels.length} grade level{t.grade_levels.length === 1 ? "" : "s"}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top">
                          {(t.assigned_classes || []).length ? (
                            <div className="flex flex-wrap gap-1.5">
                              {(t.assigned_classes || []).slice(0, MAX_TABLE_BADGES).map((c) => (
                                <span
                                  key={c.class_id}
                                  title={`${c.name || "Class"} · ${c.student_count} student${c.student_count === 1 ? "" : "s"}`}
                                  className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                                >
                                  <BookOpen className="w-3 h-3" />
                                  {[[c.grade, c.section].filter(Boolean).join(" · "), c.subject]
                                    .filter(Boolean)
                                    .join(" — ") || c.name || "Class"}
                                  <span className="text-blue-500">({c.student_count})</span>
                                </span>
                              ))}
                              {(t.assigned_classes || []).length > MAX_TABLE_BADGES && (
                                <button
                                  onClick={() => { setSelectedTeacher(t); setDetailModalOpen(true); }}
                                  className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                  +{t.assigned_classes.length - MAX_TABLE_BADGES} more
                                </button>
                              )}
                            </div>
                          ) : <span className="text-sm text-gray-400">No classes assigned</span>}
                        </td>
                        <td className="px-6 py-4 align-top">
                          {(t.departments || []).length ? (
                            <div className="flex flex-wrap gap-1.5">
                              {(t.departments || []).slice(0, MAX_TABLE_BADGES).map((d) => (
                                <span
                                  key={d}
                                  className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700"
                                >
                                  <Building className="w-3 h-3" />
                                  {d}
                                </span>
                              ))}
                              {(t.departments || []).length > MAX_TABLE_BADGES && (
                                <button
                                  onClick={() => { setSelectedTeacher(t); setDetailModalOpen(true); }}
                                  className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                  +{t.departments.length - MAX_TABLE_BADGES} more
                                </button>
                              )}
                            </div>
                          ) : <span className="text-sm text-gray-400">No department</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <ActionsCell className="justify-end">
                            <ActionIconButton
                              icon={Eye}
                              tone="view"
                              onClick={() => {
                                setSelectedTeacher(t);
                                setDetailModalOpen(true);
                              }}
                              title="View Details"
                            />

                            <ActionIconButton
                              icon={ArrowUpCircle}
                              tone="approve"
                              onClick={() => openPromote(t)}
                              title="Update Grade Levels"
                            />

                            <ActionIconButton
                              icon={Trash2}
                              tone="delete"
                              onClick={() => openDeleteConfirmation('single', t.email)}
                              title="Delete"
                            />
                          </ActionsCell>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination - exactly like LessonPlanHistory.js */}
            {!loading && !error && filteredTeachers.length > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-sm text-gray-500">
                    Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, filteredTeachers.length)} of {filteredTeachers.length} teachers
                  </div>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    className="text-sm border border-gray-300 rounded px-2 py-1 text-gray-600"
                  >
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPage(Math.max(page - 1, 1))}
                    disabled={page === 1}
                    className={`p-2 rounded-md ${page === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                    // Show pages around current page
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => setPage(pageNum)}
                        className={`px-3 py-1 rounded-md ${pageNum === page ? 'bg-[#D67C2C] text-white' : 'text-gray-600 hover:bg-gray-200'}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setPage(Math.min(page + 1, totalPages))}
                    disabled={page === totalPages}
                    className={`p-2 rounded-md ${page === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Error Modal - similar to LessonPlanHistory.js DeleteConfirmationModal */}
          <Modal
            title={
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <span>Error Loading Teachers</span>
              </div>
            }
            open={errorModalVisible}
            onCancel={() => setErrorModalVisible(false)}
            footer={[
              <button
                key="retry"
                onClick={handleRetryFetch}
                className="px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors mr-2"
              >
                Retry
              </button>,
              <button
                key="cancel"
                onClick={() => setErrorModalVisible(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>,
            ]}
          >
            <div className="py-4">
              <p className="mb-4">Failed to fetch teachers. This could be due to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Network connection issue</li>
                <li>Authentication token not fully loaded</li>
                <li>Server temporarily unavailable</li>
              </ul>
              <p className="mt-4">Please try refreshing the page or click "Retry" to attempt loading again.</p>
            </div>
          </Modal>

          {/* Add Teacher Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#D67C2C]" />
                <span>Add New Teacher</span>
              </div>
            }
            open={addModalOpen}
            onCancel={() => setAddModalOpen(false)}
            footer={null}
          >
            <form
              className="py-4"
              onSubmit={handleCreateTeacher}
            >
              <div className="space-y-5">
                <div>
                  <label className="font-medium block mb-2 text-gray-700">Full Name</label>
                  <input
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors"
                    required
                    placeholder="Enter teacher's full name"
                    value={formTeacher.name}
                    onChange={(e) =>
                      setFormTeacher((d) => ({ ...d, name: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <label className="font-medium block mb-2 text-gray-700">Email Address</label>
                  <input
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors"
                    required
                    type="email"
                    placeholder="teacher@example.com"
                    value={formTeacher.email}
                    onChange={(e) =>
                      setFormTeacher((d) => ({ ...d, email: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <label className="font-medium block mb-2 text-gray-700">Password</label>
                  <div className="relative">
                    <input
                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 pr-12 focus:outline-none focus:border-[#D67C2C] transition-colors"
                      required
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a strong password"
                      value={formTeacher.password}
                      onChange={(e) =>
                        setFormTeacher((d) => ({
                          ...d,
                          password: e.target.value,
                        }))
                      }
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-3.5 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="font-medium block mb-2 text-gray-700">Grade Levels</label>
                  <div className="border-2 border-gray-200 rounded-lg">
                    <select
                      className="w-full px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors min-h-[120px]"
                      required
                      multiple
                      value={formTeacher.grade_levels}
                      onChange={(e) =>
                        setFormTeacher((d) => ({
                          ...d,
                          grade_levels: Array.from(e.target.selectedOptions, (o) => o.value),
                        }))
                      }
                    >
                      {GRADE_LIST.map((g) => (
                        <option key={g} value={g} className="py-2">
                          Grade {g}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    Hold Ctrl (or Cmd) to select multiple grade levels
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setAddModalOpen(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-[#D67C2C] text-white rounded-lg py-3 font-semibold hover:bg-[#c06a25] transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Add Teacher
                  </button>
                </div>
              </div>
            </form>
          </Modal>

          {/* CSV Upload Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-[#D67C2C]" />
                <span>Bulk Upload Teachers</span>
              </div>
            }
            open={csvModalOpen}
            onCancel={() => setCSVModalOpen(false)}
            footer={null}
          >
            <form
              className="py-4"
              onSubmit={handleBulkUpload}
            >
              <div className="bg-[#FFF5EB] p-4 rounded-lg mb-6">
                <div className="text-[#D67C2C] font-medium mb-2">Required CSV Format:</div>
                <div className="font-mono text-sm bg-white p-2 rounded border border-[#FFD1A8]">
                  name,email,password,grade_levels
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  Note: For multiple grade levels, use format: "4,5,6"
                </div>
              </div>

              <div className="mb-6">
                <label className="font-medium block mb-2 text-gray-700">Upload CSV File</label>
                <div className="flex flex-col gap-4">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVChange}
                    className="border-2 border-dashed border-gray-200 p-4 rounded-lg text-gray-700 focus:outline-none focus:border-[#D67C2C] transition-colors cursor-pointer"
                  />
                  <button
                    type="button"
                    onClick={downloadCSVTemplate}
                    className="inline-flex gap-2 text-[#D67C2C] hover:text-[#c06a25] transition-colors items-center text-sm font-medium"
                  >
                    <FileDown className="w-4 h-4" /> Download Template CSV
                  </button>
                </div>
              </div>

              {/* Validation Summary */}
              {csvValidation && (
                <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="font-medium text-gray-700 mb-3">Validation Summary</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span>Valid: <strong>{csvValidation.valid_count || 0}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span>Invalid: <strong>{csvValidation.invalid_emails?.length || 0}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <span>Duplicates: <strong>{csvValidation.duplicate_emails_in_csv?.length || 0}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                      <span>Existing: <strong>{csvValidation.existing_emails_in_db?.length || 0}</strong></span>
                    </div>
                  </div>
                  {csvValidation.summary && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-green-600 font-medium">
                        ✓ {csvValidation.summary.can_upload} will be uploaded
                      </span>
                      {csvValidation.summary.will_skip > 0 && (
                        <span className="text-orange-600 ml-4">
                          ⚠ {csvValidation.summary.will_skip} will be skipped
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {validating && (
                <div className="mb-4 flex items-center gap-2 text-gray-600">
                  <div className="w-4 h-4 animate-spin rounded-full border-2 border-[#D67C2C] border-t-transparent"></div>
                  <span>Validating CSV...</span>
                </div>
              )}

              {csvPreview.length > 0 && (
                <div className="mb-6">
                  <div className="font-medium mb-2 text-gray-700">Preview (First 20 rows):</div>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm overflow-x-auto max-h-60 border border-gray-200">
                    <div className="font-mono font-semibold text-gray-700 py-2 border-b-2 border-gray-300 flex gap-4">
                      <span className="w-10">Row</span>
                      <span className="flex-1">Name</span>
                      <span className="flex-1">Email</span>
                      <span className="w-32">Status</span>
                    </div>
                    {csvPreview.map((row, i) => {
                      const statusColors = {
                        valid: "bg-green-50 border-l-4 border-green-500",
                        invalid_email: "bg-red-50 border-l-4 border-red-500",
                        duplicate: "bg-yellow-50 border-l-4 border-yellow-500",
                        exists: "bg-orange-50 border-l-4 border-orange-500"
                      };
                      const statusLabels = {
                        valid: <span className="text-green-600">✓ Valid</span>,
                        invalid_email: <span className="text-red-600">✗ {row.reason}</span>,
                        duplicate: <span className="text-yellow-600">⚠ Duplicate</span>,
                        exists: <span className="text-orange-600">⚠ Exists</span>
                      };

                      return (
                        <div
                          key={i}
                          className={`font-mono py-2 border-b last:border-0 flex gap-4 items-center ${statusColors[row.status] || ""}`}
                        >
                          <span className="w-10 text-gray-500">{row.row}</span>
                          <span className="flex-1 truncate">{row.name || "-"}</span>
                          <span className="flex-1 truncate">{row.email || "-"}</span>
                          <span className="w-32 text-xs">
                            {statusLabels[row.status] || row.status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}


              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setCSVModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-[#D67C2C] text-white rounded-lg py-3 font-semibold hover:bg-[#c06a25] transition-colors flex items-center justify-center gap-2"
                  disabled={!csvFile}
                >
                  <Upload className="w-5 h-5" />
                  Upload Teachers
                </button>
              </div>
            </form>
          </Modal>

          {/* Update Grade Levels Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <ArrowUpCircle className="w-5 h-5 text-[#D67C2C]" />
                <span>Update Grade Levels</span>
              </div>
            }
            open={promoteModalOpen}
            onCancel={() => setPromoteModalOpen(false)}
            footer={null}
          >
            <form
              className="py-4"
              onSubmit={handlePromote}
            >
              <div className="mb-6 p-4 bg-[#FFF5EB] rounded-lg">
                <div className="flex items-center gap-4">
                  {selectedTeacher?.profile_picture_url ? (
                    <img
                      src={server_url + selectedTeacher.profile_picture_url}
                      alt={selectedTeacher.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-md"
                    />
                  ) : (
                    <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center text-[#D67C2C] shadow-md">
                      <User2 className="w-6 h-6" />
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-[#333]">{selectedTeacher?.name}</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {selectedTeacher?.grade_levels && selectedTeacher?.grade_levels.length
                        ? selectedTeacher.grade_levels.map(grade => (
                          <span key={grade} className="px-2 py-1 bg-white text-[#D67C2C] rounded-full text-xs inline-flex items-center">
                            <GraduationCap className="w-3 h-3 mr-1" />
                            Grade {grade}
                          </span>
                        ))
                        : <span className="text-gray-500">No grade levels assigned</span>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="font-medium block mb-2 text-gray-700">New Grade Levels</label>
                <div className="border-2 border-gray-200 rounded-lg">
                  <select
                    className="w-full px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors min-h-[160px]"
                    multiple
                    required
                    value={promoteGrades}
                    onChange={(e) =>
                      setPromoteGrades(
                        Array.from(e.target.selectedOptions, (o) => o.value)
                      )
                    }
                  >
                    {GRADE_LIST.map((g) => (
                      <option key={g} value={g} className="py-2">
                        Grade {g}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  Hold Ctrl (or Cmd) to select multiple grade levels
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setPromoteModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-[#D67C2C] text-white rounded-lg py-3 font-semibold hover:bg-[#c06a25] transition-colors flex items-center justify-center gap-2"
                  disabled={!promoteGrades.length}
                >
                  <ArrowUpCircle className="w-5 h-5" />
                  Update Grade Levels
                </button>
              </div>
            </form>
          </Modal>

          {/* Bulk Edit Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-[#D67C2C]" />
                <span>Bulk Edit Grade Levels</span>
              </div>
            }
            open={bulkEditModalOpen}
            onCancel={() => setBulkEditModalOpen(false)}
            footer={null}
          >
            <form
              className="py-4"
              onSubmit={handleBulkEdit}
            >
              <div className="mb-6 p-4 bg-orange-50 rounded-lg">
                <div className="text-orange-700 font-semibold mb-2">
                  {selectedTeachers.length} teachers selected
                </div>
                <div className="text-sm text-orange-600">
                  This will update grade levels for all selected teachers
                </div>
              </div>

              <div className="mb-6">
                <label className="font-medium block mb-2 text-gray-700">New Grade Levels</label>
                <div className="border-2 border-gray-200 rounded-lg">
                  <select
                    className="w-full px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors min-h-[160px]"
                    multiple
                    required
                    value={bulkEditGrades}
                    onChange={(e) =>
                      setBulkEditGrades(
                        Array.from(e.target.selectedOptions, (o) => o.value)
                      )
                    }
                  >
                    {GRADE_LIST.map((g) => (
                      <option key={g} value={g} className="py-2">
                        Grade {g}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  Hold Ctrl (or Cmd) to select multiple grade levels
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setBulkEditModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-[#D67C2C] text-white rounded-lg py-3 font-semibold hover:bg-[#c06a25] transition-colors flex items-center justify-center gap-2"
                  disabled={!bulkEditGrades.length}
                >
                  <Edit3 className="w-5 h-5" />
                  Update All Selected
                </button>
              </div>
            </form>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <span>Confirm Deletion</span>
              </div>
            }
            open={deleteConfirmModal.open}
            onCancel={() => setDeleteConfirmModal({ open: false, type: 'single', email: '', teachers: [], step: 1 })}
            footer={null}
          >
            <div className="py-4">
              {deleteConfirmModal.step === 1 && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-red-500" />
                  </div>
                  <p className="text-gray-700 mb-4">
                    {deleteConfirmModal.type === 'single'
                      ? 'Are you sure you want to delete this teacher?'
                      : `Are you sure you want to delete ${deleteConfirmModal.teachers.length} teachers?`
                    }
                  </p>
                  <p className="text-sm text-gray-500">
                    This action cannot be undone. Click "Continue" to proceed.
                  </p>
                </div>
              )}

              {deleteConfirmModal.step === 2 && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-yellow-500" />
                  </div>
                  <p className="text-gray-700 mb-4 font-semibold">
                    Final Warning!
                  </p>
                  <p className="text-sm text-gray-500">
                    {deleteConfirmModal.type === 'single'
                      ? 'The teacher and all associated data will be permanently deleted.'
                      : `All ${deleteConfirmModal.teachers.length} teachers and their associated data will be permanently deleted.`
                    }
                  </p>
                </div>
              )}

              {deleteConfirmModal.step === 3 && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Trash2 className="w-8 h-8 text-red-500" />
                  </div>
                  <p className="text-gray-700 mb-4 font-bold text-red-600">
                    Last Chance!
                  </p>
                  <p className="text-sm text-gray-500">
                    Click "Delete Forever" to permanently remove the data.
                  </p>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setDeleteConfirmModal({ open: false, type: 'single', email: '', teachers: [], step: 1 })}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirmation}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors ${deleteConfirmModal.step === 3
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-[#D67C2C] hover:bg-[#c06a25] text-white'
                    }`}
                >
                  {deleteConfirmModal.step === 3 ? 'Delete Forever' : 'Continue'}
                </button>
              </div>
            </div>
          </Modal>

          {/* Teacher Detail Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <User2 className="w-5 h-5 text-[#D67C2C]" />
                <span>Teacher Details</span>
              </div>
            }
            open={detailModalOpen}
            onCancel={() => setDetailModalOpen(false)}
            footer={[
              <button
                key="close"
                onClick={() => setDetailModalOpen(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            ]}
          >
            <div className="flex flex-col md:flex-row items-start gap-6 mb-8">
              <div className="flex-shrink-0">
                {selectedTeacher?.profile_picture_url ? (
                  <img
                    src={server_url + selectedTeacher.profile_picture_url}
                    alt={selectedTeacher.name}
                    className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="bg-[#FFF5EB] rounded-full w-28 h-28 flex items-center justify-center text-[#D67C2C] shadow-lg">
                    <User2 className="w-14 h-14" />
                  </div>
                )}
              </div>

              <div className="flex-grow">
                <h3 className="text-2xl font-bold text-[#333] mb-1">{selectedTeacher?.name || "--"}</h3>
                <div className="text-gray-500 mb-3">{selectedTeacher?.email || "--"}</div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedTeacher?.grade_levels && selectedTeacher?.grade_levels.length
                    ? selectedTeacher.grade_levels.map(grade => (
                      <div key={grade} className="px-3 py-1 bg-[#FFF5EB] text-[#D67C2C] rounded-full text-sm inline-flex items-center">
                        <GraduationCap className="w-4 h-4 mr-1" />
                        Grade {grade}
                      </div>
                    ))
                    : <div className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-sm">No grade levels</div>
                  }
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setDetailModalOpen(false);
                      openPromote(selectedTeacher);
                    }}
                    className="px-3 py-1.5 bg-white border-2 border-[#D67C2C] text-[#D67C2C] rounded-lg text-sm font-medium hover:bg-[#FFF5EB] transition-colors inline-flex items-center gap-1"
                  >
                    <ArrowUpCircle className="w-4 h-4" />
                    Update Grade Levels
                  </button>

                  <button
                    onClick={() => {
                      setDetailModalOpen(false);
                      openDeleteConfirmation('single', selectedTeacher.email);
                    }}
                    className="px-3 py-1.5 bg-white border-2 border-red-500 text-red-500 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors inline-flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>

            {/* Secondary details, moved off the table so it stays scannable. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              {[
                { label: "Experience", icon: <Clock className="w-4 h-4" />, value:
                    selectedTeacher?.years_of_experience !== null && selectedTeacher?.years_of_experience !== undefined
                      ? `${selectedTeacher.years_of_experience} yrs` : null },
                { label: "Current School", icon: <Building className="w-4 h-4" />, value: selectedTeacher?.current_school || null },
                { label: "Phone", icon: <User2 className="w-4 h-4" />, value: selectedTeacher?.phone_number || null },
              ].map(({ label, icon, value }) => (
                <div key={label} className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {icon}{label}
                  </div>
                  <div className={`mt-1 text-sm font-medium ${value ? "text-gray-900" : "text-gray-400"}`}>
                    {value || "Not recorded"}
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-6">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Current Classes ({(selectedTeacher?.assigned_classes || []).length})
              </div>
              {(selectedTeacher?.assigned_classes || []).length ? (
                <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
                  {selectedTeacher.assigned_classes.map((c) => (
                    <span
                      key={c.class_id}
                      title={c.name || "Class"}
                      className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
                    >
                      <BookOpen className="w-3 h-3" />
                      {[[c.grade, c.section].filter(Boolean).join(" · "), c.subject]
                        .filter(Boolean)
                        .join(" — ") || c.name || "Class"}
                      <span className="text-blue-500">({c.student_count})</span>
                    </span>
                  ))}
                </div>
              ) : <div className="text-sm text-gray-400">No classes assigned</div>}
            </div>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Departments ({(selectedTeacher?.departments || []).length})
              </div>
              {(selectedTeacher?.departments || []).length ? (
                <div className="flex flex-wrap gap-1.5">
                  {selectedTeacher.departments.map((d) => (
                    <span key={d} className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700">
                      <Building className="w-3 h-3" />
                      {d}
                    </span>
                  ))}
                </div>
              ) : <div className="text-sm text-gray-400">No department assigned</div>}
            </div>
          </Modal>

          {/* Skipped Records Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                <span>Skipped Records</span>
              </div>
            }
            open={showSkippedModal}
            onCancel={() => setShowSkippedModal(false)}
            footer={
              <button
                onClick={() => setShowSkippedModal(false)}
                className="px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
              >
                Close
              </button>
            }
            width={600}
          >
            {uploadResult?.skipped && uploadResult.skipped.length > 0 ? (
              <div>
                <p className="text-gray-600 mb-4">
                  The following {uploadResult.skipped.length} record(s) were skipped during upload:
                </p>
                <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Email</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Name</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.skipped.map((item, idx) => (
                        <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-600">{item.email}</td>
                          <td className="px-4 py-2 text-gray-600">{item.name || "-"}</td>
                          <td className="px-4 py-2">
                            <span className="text-red-600 text-xs bg-red-50 px-2 py-1 rounded">
                              {item.reason}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                  <strong>Tip:</strong> Fix these issues in your CSV file and re-upload the corrected records.
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No skipped records to display.</p>
            )}
          </Modal>
        </div>
      </div>
    </>
  );
}

export default TeacherView;